import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  ANLAESSE,
  KUNDENARTEN,
  aendern as sendeAenderung,
  postfaecher as holePostfaecher,
  regelEntfernen as sendeRegelEntfernen,
  regelSetzen as sendeRegelSetzen,
  regeln as holeRegeln,
  type Anlass,
  type Kundenart,
  type Postfach,
  type Postfachregel,
} from '../api/postfaecher';
import { ApiFehler } from '../api/fehler';
import { posteingangsstand, type Posteingangsstand } from '../api/support';
import { rollen as holeRollen, type Rolle } from '../api/verwaltung';
import { markenklasse } from '../marken';
import { Feldraster, Feldzeile } from '../komponenten/Akte';
import { Dialog } from '../komponenten/Dialog';
import { Einstellungsseite, Thema } from '../komponenten/Einstellungen';
import { Meldung } from '../komponenten/Meldung';
import { DemoMail } from './DemoMail';

/**
 * Die Postfächer des Hauses (SUP-19, SYS-10).
 *
 * Das Haus versendet aus mehreren: Vertriebspost an Firmenkunden unter der
 * einen Adresse, Support und Allgemeines unter der anderen. **Welches wofür**
 * steht in der Regeltabelle darunter und nicht im Code — sonst wäre jede
 * Änderung daran eine Auslieferung.
 *
 * WAS DIESE MASKE NICHT KANN: Kennwörter setzen. Sie stehen in der
 * Betriebskonfiguration, und die Maske zeigt nur, **wie die Variablen heißen**
 * und ob sie gefunden wurden. Ein Kennwort, das hier eingegeben würde, müsste
 * irgendwo liegen — und der einzige Ort wäre die Datenbank, also jede
 * Sicherung und jeder Prüfungsauszug (BP-05).
 *
 * ZWEI ZUSTÄNDE, DIE NICHT DASSELBE SIND und deshalb getrennt angezeigt
 * werden: **freigegeben** ist die fachliche Entscheidung und wird hier
 * gesetzt; **versandbereit** sagt, ob der Dienst die Zugangsdaten gefunden
 * hat. Ein Postfach kann freigegeben und trotzdem nicht versandbereit sein —
 * dann fehlt ein Eintrag in der Betriebskonfiguration, kein Haken hier.
 *
 * BEARBEITUNG DER VORGÄNGE (Auftrag CRM-178): je Postfach Reaktions- und
 * Lösungsziel in Stunden und die Rolle, die ein überschrittenes Ziel erfährt
 * (SUP-25), und ob eingehende Mails eine Eingangsbestätigung mit der
 * Vorgangsnummer bekommen (SUP-27, Vorgabe aus).
 */

/** SUP-25: Grenzen wie in der Datenbank (0333). */
const REAKTION_HOECHSTENS = 720;
const LOESUNG_HOECHSTENS = 2160;
const BESTAETIGUNG_HOECHSTENS = 4000;

/** Stunden aus der Eingabe: leer ist „kein Ziel", sonst eine ganze Zahl von 1 bis `hoechstens`. */
function alsStunden(text: string, hoechstens: number): number | null | 'falsch' {
  const t = text.trim();
  if (t === '') return null;
  if (!/^\d{1,4}$/.test(t)) return 'falsch';
  const n = Number(t);
  return n >= 1 && n <= hoechstens ? n : 'falsch';
}

/** SUP-25: Fehler der beiden Ziele, je Feld; leer heißt in Ordnung. */
export function zielfehler(reaktion: string, loesung: string): { readonly reaktion: string; readonly loesung: string } {
  const r = alsStunden(reaktion, REAKTION_HOECHSTENS);
  const l = alsStunden(loesung, LOESUNG_HOECHSTENS);
  return {
    reaktion: r === 'falsch' ? `Ganze Stunden von 1 bis ${String(REAKTION_HOECHSTENS)}.` : '',
    loesung: l === 'falsch'
      ? `Ganze Stunden von 1 bis ${String(LOESUNG_HOECHSTENS)}.`
      : typeof r === 'number' && typeof l === 'number' && l < r
        ? `Nicht kürzer als das Reaktionsziel (${String(r)} Std.).`
        : '',
  };
}

function stundenText(n: number | null): string {
  return n === null ? '' : String(n);
}

export function Postfaecher({ darfPflegen }: { readonly darfPflegen: boolean }): JSX.Element {
  const [liste, setzeListe] = useState<readonly Postfach[]>([]);
  const [regelliste, setzeRegeln] = useState<readonly Postfachregel[]>([]);
  const [offen, setzeOffen] = useState<string | undefined>(undefined);
  const [entwurf, setzeEntwurf] = useState<Postfach | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [eingang, setzeEingang] = useState<readonly Posteingangsstand[]>([]);
  /** SUP-25: die Ziele als Text, solange sie getippt werden. */
  const [ziele, setzeZiele] = useState({ reaktion: '', loesung: '' });
  /** SUP-25: die Rollen zur Wahl der Eskalation; leer, wenn sie nicht zu lesen sind. */
  const [rollenliste, setzeRollenliste] = useState<readonly Rolle[]>([]);
  /** CRM-183: das Postfach, in das gerade eine Demo-Mail eingespielt wird. */
  const [demoFuer, setzeDemoFuer] = useState<Postfach | undefined>(undefined);

  const laden =useCallback(async (): Promise<void> => {
    const [p, r] = await Promise.all([holePostfaecher(), holeRegeln()]);
    setzeListe(p);
    setzeRegeln(r);
  }, []);

  useEffect(() => {
    void laden().catch((ursache: unknown) => setzeFehler(ursache as ApiFehler));
  }, [laden]);

  /*
   * SUP-01 (Auftrag CRM-176): der Abruf je Postfach. Er verlangt
   * `support:sehen`; wer das nicht hat, sieht die Zeile nicht — kein Fehler,
   * denn die Maske selbst gehoert dem Versand.
   */
  const eingangLaden = useCallback((): void => {
    posteingangsstand().then(
      (antwort) => {
        setzeEingang(antwort.postfaecher);
      },
      () => {
        setzeEingang([]);
      },
    );
  }, []);

  useEffect(() => {
    eingangLaden();
  }, [eingangLaden]);

  /* SUP-25: die Rollen erst, wenn jemand pflegen darf — sonst gibt es keine Wahl. */
  useEffect(() => {
    if (!darfPflegen) return;
    holeRollen().then(
      (antwort) => {
        setzeRollenliste(antwort);
      },
      () => {
        setzeRollenliste([]);
      },
    );
  }, [darfPflegen]);

  const rollenname = (kennung: string): string =>
    rollenliste.find((r) => r.kennung === kennung)?.bezeichnung ?? kennung;

  async function handle(was: () => Promise<void>): Promise<void> {
    setzeLaeuft(true);
    try {
      await was();
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  /**
   * Die Spalten der Regeltabelle. `null` ist die allgemeine Regel — sie steht
   * VORN, weil sie die ist, die auffängt: Jede Kundenart ohne eigene Regel
   * folgt ihr, und wer sie ändert, ändert den Normalfall.
   */
  const SPALTEN: readonly (Kundenart | null)[] = [null, ...KUNDENARTEN];

  /** Die Regel, die für eine Zelle gilt — besondere vor allgemeiner. */
  function regelFuer(anlass: Anlass, kundenart: Kundenart | null): Postfachregel | undefined {
    return (
      (kundenart === null
        ? undefined
        : regelliste.find((r) => r.anlass === anlass && r.kundenart === kundenart))
      ?? regelliste.find((r) => r.anlass === anlass && r.kundenart === null)
    );
  }

  function istBesonders(anlass: Anlass, kundenart: Kundenart | null): boolean {
    return kundenart !== null
      && regelliste.some((r) => r.anlass === anlass && r.kundenart === kundenart);
  }

  const fehlerZiele = zielfehler(ziele.reaktion, ziele.loesung);

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      {/*
        SEITENTYP EINSTELLUNGEN (ÄA-41): je Postfach eine Karte, darunter über
        die ganze Breite die Regeltabelle. Die Reiter der Gruppe „Umsysteme"
        stehen im Seitenkopf.
      */}
      <Einstellungsseite>
        {liste.map((p) => (
          <Thema key={p.id} titel={p.bezeichnung} zusatz={p.kennung}>
            {/* SUP-19 */}
            <Feldraster>
              <Feldzeile
                beschriftung="Absender"
                wert={p.absenderAdresse}
                anzeige={`${p.absenderName} <${p.absenderAdresse}>`}
              />
              <Feldzeile beschriftung="Antwort an" wert={p.antwortAn} />
              <Feldzeile
                beschriftung="Zugang"
                wert={p.zugangsart}
                anzeige={
                  /*
                    CRM-183: Die Demo-Anbindung stellt nie zu — das muss hier
                    jeder sehen. Gesperrt (Demo außerhalb einer Demo-Instanz)
                    ist sie KEINE Demo-Anbindung, sondern gar keine.
                  */
                  p.hinweis !== null ? (
                    <>
                      <span className={markenklasse('sackgasse')}>nicht angebunden</span>
                      <span className="feld__fehler"> {p.hinweis}</span>
                    </>
                  ) : p.anbindung === 'demo' ? (
                    <>
                      <span className={markenklasse('aufmerksamkeit')}>Demo-Anbindung</span>
                      <span className="kanalliste__name"> · stellt nicht zu · {p.erwarteteVariablen[0]}</span>
                    </>
                  ) : (
                    <>
                      {p.zugangsart === 'graph' ? 'Microsoft Graph' : 'SMTP'}
                      <span className="kanalliste__name"> · {p.erwarteteVariablen[0]}</span>
                    </>
                  )
                }
              />
              <Feldzeile
                beschriftung="Zustand"
                wert={null}
                anzeige={
                  <>
                    {p.aktiv ? (
                      <span className={markenklasse('laufend')}>freigegeben</span>
                    ) : (
                      <span className={markenklasse('stillgelegt')}>nicht freigegeben</span>
                    )}{' '}
                    {/*
                      ERR-01: Freigegeben, aber ohne Zugangsdaten ist eine
                      Störung — der Postausgang nimmt an, und nichts geht
                      hinaus. Nicht freigegeben ist es nur unvollständig.
                    */}
                    {p.versandbereit ? (
                      <span className={markenklasse('abschluss')}>
                        {p.anbindung === 'demo' ? 'versandbereit (Demo)' : 'versandbereit'}
                      </span>
                    ) : (
                      <span className={markenklasse(p.aktiv ? 'sackgasse' : 'stillgelegt')}>
                        ohne Zugangsdaten
                      </span>
                    )}
                  </>
                }
              />
              <Eingangszeile stand={eingang.find((e) => e.kennung === p.kennung)} />
              {/* SUP-25 */}
              <Feldzeile
                beschriftung="Ziele"
                wert={null}
                anzeige={
                  p.reaktionszielStunden === null && p.loesungszielStunden === null
                    ? 'keine'
                    : [
                      p.reaktionszielStunden === null ? null : `Reaktion ${String(p.reaktionszielStunden)} Std.`,
                      p.loesungszielStunden === null ? null : `Lösung ${String(p.loesungszielStunden)} Std.`,
                      p.eskalationRolle === null ? 'ohne Eskalation' : `Eskalation an ${rollenname(p.eskalationRolle)}`,
                    ].filter((t) => t !== null).join(' · ')
                }
              />
              {/* SUP-27 */}
              <Feldzeile
                beschriftung="Eingangsbestätigung"
                wert={null}
                anzeige={
                  p.eingangsbestaetigung
                    ? `an, ${p.eingangsbestaetigungText === null ? 'Vorgabetext' : 'eigener Text'}`
                    : 'aus'
                }
              />
              {p.bemerkung !== null && <Feldzeile beschriftung="Bemerkung" wert={p.bemerkung} />}
            </Feldraster>

            {darfPflegen && (
              <div className="knopfreihe">
                <button
                  type="button"
                  className="knopf knopf--klein"
                  onClick={() => {
                    setzeOffen(p.id);
                    setzeEntwurf(p);
                    setzeZiele({
                      reaktion: stundenText(p.reaktionszielStunden),
                      loesung: stundenText(p.loesungszielStunden),
                    });
                  }}
                >
                  Bearbeiten …
                </button>
                {p.anbindung === 'demo' && p.hinweis === null && (
                  <button type="button" className="knopf knopf--klein" onClick={() => setzeDemoFuer(p)}>
                    Demo-Mail einspielen …
                  </button>
                )}
              </div>
            )}
          </Thema>
        ))}

        <Thema titel="Welcher Anlass geht aus welchem Postfach" breit>
          {/*
            Die Tabelle zeigt für jede Zelle das Postfach, das TATSÄCHLICH
            gewählt würde — nicht nur die eingetragenen Regeln. Eine Zelle ohne
            eigene Regel erbt die allgemeine, und das steht dort auch. Wer nur
            die Einträge sähe, hielte eine leere Zelle für „geht nirgends hin".
          */}
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <thead>
                <tr>
                  <th scope="col">Anlass</th>
                  {SPALTEN.map((k) => (
                    <th key={k ?? 'allgemein'} scope="col">
                      {k ?? 'alle Kundenarten'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ANLAESSE.map((anlass) => (
                  <tr key={anlass}>
                    <th scope="row">{anlass}</th>
                    {SPALTEN.map((kundenart) => {
                      const regel = regelFuer(anlass, kundenart);
                      const besonders = istBesonders(anlass, kundenart);
                      const allgemein = kundenart === null;
                      return (
                        <td key={kundenart ?? 'allgemein'}>
                          {darfPflegen ? (
                            <select
                              aria-label={
                                allgemein ? `${anlass} allgemein` : `${anlass} an ${String(kundenart)}`
                              }
                              value={allgemein || besonders ? (regel?.postfach ?? '') : ''}
                              disabled={laeuft}
                              onChange={(e) =>
                                void handle(async () => {
                                  /*
                                    Die leere Wahl entfernt eine BESONDERE Regel.
                                    Bei der allgemeinen gibt es sie nicht: Ohne
                                    allgemeine Regel wuerde jede Nachricht dieses
                                    Anlasses abgewiesen.
                                  */
                                  if (e.target.value === '') {
                                    if (kundenart === null) return;
                                    setzeRegeln(await sendeRegelEntfernen(anlass, kundenart));
                                    return;
                                  }
                                  setzeRegeln(
                                    await sendeRegelSetzen({
                                      anlass,
                                      kundenart,
                                      postfach: e.target.value,
                                    }),
                                  );
                                })
                              }
                            >
                              {!allgemein && (
                                <option value="">
                                  {regel === undefined
                                    ? '— keine Regel —'
                                    : `wie allgemein (${regel.kennung})`}
                                </option>
                              )}
                              {liste.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.kennung}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <>
                              {regel?.kennung ?? '—'}
                              {!besonders && regel !== undefined && (
                                <span className="kanalliste__name"> (allgemein)</span>
                              )}
                            </>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="feld__hilfe">
            „wie allgemein" heißt: Für diese Kundenart gilt keine eigene Regel, es greift die
            allgemeine des Anlasses. Eine eigene Regel setzt man, indem man hier ein Postfach wählt;
            zurück auf „wie allgemein" entfernt sie wieder. Die allgemeine Regel selbst lässt sich
            hier nicht löschen — ohne sie würde jede Nachricht dieses Anlasses abgewiesen.
          </p>
        </Thema>
      </Einstellungsseite>

      <p className="hilfe">
        Kennung und Zugangsschlüssel stehen fest: Aus dem Schlüssel bildet der Dienst die Namen
        seiner Umgebungsvariablen. Die Kennwörter selbst stehen in der Betriebskonfiguration und
        nie in der Datenbank — sie wären sonst in jeder Sicherung und in jedem Auszug für die
        Betriebsprüfung.
      </p>

      {/*
        DAS FORMULAR STEHT IM FENSTER UND NICHT UNTER DER TABELLE.
        Bei sechs Postfächern wäre das noch zu finden; die Überlegung gilt
        trotzdem: Wer eine Zeile anfasst, soll dort landen, wo der Blick ist —
        und beim Ausfüllen nicht überlegen müssen, welche Zeile gemeint war.
      */}
      {offen !== undefined && entwurf !== undefined && (
        <Dialog
          titel={entwurf.bezeichnung}
          unterzeile={entwurf.kennung}
          beiSchliessen={() => {
            setzeOffen(undefined);
            setzeEntwurf(undefined);
          }}
        >
          <div className="hinweiskarte">
            <p>
              Erwartete Umgebungsvariablen:{' '}
              {entwurf.erwarteteVariablen.map((v, i) => (
                <span key={v}>
                  {i > 0 && ', '}
                  <code>{v}</code>
                </span>
              ))}
            </p>
            <p className="feld__hilfe">
              {entwurf.versandbereit
                ? 'Der Dienst hat sie gefunden.'
                : 'Der Dienst hat sie nicht gefunden — ohne sie lässt sich das Postfach nicht freigeben.'}
            </p>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="pf-bezeichnung">
              Bezeichnung
            </label>
            <input
              id="pf-bezeichnung"
              type="text"
              value={entwurf.bezeichnung}
              onChange={(e) => setzeEntwurf({ ...entwurf, bezeichnung: e.target.value })}
            />
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="pf-adresse">
              Absenderadresse
            </label>
            <input
              id="pf-adresse"
              type="email"
              value={entwurf.absenderAdresse}
              onChange={(e) => setzeEntwurf({ ...entwurf, absenderAdresse: e.target.value })}
            />
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="pf-name">
              Absendername
            </label>
            <input
              id="pf-name"
              type="text"
              value={entwurf.absenderName}
              onChange={(e) => setzeEntwurf({ ...entwurf, absenderName: e.target.value })}
            />
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="pf-antwort">
              Antwort an (optional)
            </label>
            <input
              id="pf-antwort"
              type="email"
              value={entwurf.antwortAn ?? ''}
              onChange={(e) =>
                setzeEntwurf({
                  ...entwurf,
                  antwortAn: e.target.value === '' ? null : e.target.value,
                })
              }
            />
            <p className="feld__hilfe">
              Wohin Antworten gehen sollen, wenn das nicht die Absenderadresse ist.
            </p>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="pf-zugangsart">
              Zugangsart
            </label>
            <select
              id="pf-zugangsart"
              value={entwurf.zugangsart}
              onChange={(e) =>
                setzeEntwurf({ ...entwurf, zugangsart: e.target.value as 'smtp' | 'graph' })
              }
            >
              <option value="smtp">SMTP (mailcow)</option>
              <option value="graph">Microsoft Graph</option>
            </select>
            <p className="feld__hilfe">
              Je Postfach und nicht für das ganze Haus: Ein Postfach kann auf Graph umziehen,
              während das andere bei mailcow bleibt. Die Umstellung ist nur an einem Postfach
              möglich, das nicht freigegeben ist — sonst würde gegen die Variablen der alten Art
              geprüft und mit denen der neuen gesendet.
            </p>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="pf-aktiv">
              Freigegeben
            </label>
            <input
              id="pf-aktiv"
              type="checkbox"
              checked={entwurf.aktiv}
              onChange={(e) => setzeEntwurf({ ...entwurf, aktiv: e.target.checked })}
            />
            {/* ERR-01 */}
            <p className="feld__hilfe">
              Solange nicht freigegeben, nimmt der Postausgang für dieses Postfach nichts an. Die
              Meldung fällt dann beim Einstellen — dort sitzt noch jemand davor.
            </p>
          </div>

          {/* SUP-25: Ziele und Eskalation. Leer heißt: kein Ziel. */}
          <div className="feldreihe">
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="pf-reaktion">
                Reaktionsziel (Stunden)
              </label>
              <input
                id="pf-reaktion"
                type="text"
                inputMode="numeric"
                className="feld__eingabe feld__eingabe--schmal"
                value={ziele.reaktion}
                aria-invalid={fehlerZiele.reaktion !== ''}
                aria-describedby="pf-reaktion-hilfe"
                onChange={(e) => setzeZiele({ ...ziele, reaktion: e.target.value })}
              />
              <p className="feld__hilfe" id="pf-reaktion-hilfe">
                {fehlerZiele.reaktion !== '' ? fehlerZiele.reaktion : 'Bis zur ersten Antwort. Leer: kein Ziel.'}
              </p>
            </div>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="pf-loesung">
                Lösungsziel (Stunden)
              </label>
              <input
                id="pf-loesung"
                type="text"
                inputMode="numeric"
                className="feld__eingabe feld__eingabe--schmal"
                value={ziele.loesung}
                aria-invalid={fehlerZiele.loesung !== ''}
                aria-describedby="pf-loesung-hilfe"
                onChange={(e) => setzeZiele({ ...ziele, loesung: e.target.value })}
              />
              <p className="feld__hilfe" id="pf-loesung-hilfe">
                {fehlerZiele.loesung !== '' ? fehlerZiele.loesung : 'Bis der Vorgang gelöst ist. Leer: kein Ziel.'}
              </p>
            </div>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="pf-eskalation">
              Eskalation an
            </label>
            <select
              id="pf-eskalation"
              value={entwurf.eskalationRolle ?? ''}
              onChange={(e) =>
                setzeEntwurf({ ...entwurf, eskalationRolle: e.target.value === '' ? null : e.target.value })
              }
            >
              <option value="">— niemanden —</option>
              {rollenliste.map((r) => (
                <option key={r.kennung} value={r.kennung}>
                  {r.bezeichnung}
                </option>
              ))}
              {/* Eine gesetzte Rolle bleibt sichtbar, auch wenn die Liste sie nicht (mehr) führt. */}
              {entwurf.eskalationRolle !== null && !rollenliste.some((r) => r.kennung === entwurf.eskalationRolle) && (
                <option value={entwurf.eskalationRolle}>{entwurf.eskalationRolle}</option>
              )}
            </select>
            <p className="feld__hilfe">
              Wer es in der Glocke erfährt, wenn ein Vorgang dieses Postfachs sein Ziel überschreitet.
            </p>
          </div>

          {/* SUP-27: Vorgabe aus. */}
          <div className="feld">
            <label className="liste__schalter">
              <input
                type="checkbox"
                checked={entwurf.eingangsbestaetigung}
                onChange={(e) => setzeEntwurf({ ...entwurf, eingangsbestaetigung: e.target.checked })}
              />
              <span>Eingangsbestätigung senden</span>
            </label>
            <p className="feld__hilfe">
              Eine neue Mail, aus der ein Vorgang entsteht, bekommt eine kurze Antwort mit der
              Vorgangsnummer. Aussortiertes bekommt keine.
            </p>
          </div>

          {entwurf.eingangsbestaetigung && (
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="pf-bestaetigung">
                Text der Eingangsbestätigung (optional)
              </label>
              <textarea
                id="pf-bestaetigung"
                rows={5}
                maxLength={BESTAETIGUNG_HOECHSTENS}
                value={entwurf.eingangsbestaetigungText ?? ''}
                onChange={(e) =>
                  setzeEntwurf({
                    ...entwurf,
                    eingangsbestaetigungText: e.target.value === '' ? null : e.target.value,
                  })
                }
              />
              <p className="feld__hilfe">
                {'{{vorgang.nummer}}'} setzt die Vorgangsnummer ein. Leer: der Vorgabetext des CRM.
              </p>
            </div>
          )}

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="pf-bemerkung">
              Bemerkung
            </label>
            <textarea
              id="pf-bemerkung"
              rows={2}
              value={entwurf.bemerkung ?? ''}
              onChange={(e) =>
                setzeEntwurf({
                  ...entwurf,
                  bemerkung: e.target.value === '' ? null : e.target.value,
                })
              }
            />
          </div>

          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              onClick={() => {
                setzeOffen(undefined);
                setzeEntwurf(undefined);
              }}
            >
              Abbrechen
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={laeuft || fehlerZiele.reaktion !== '' || fehlerZiele.loesung !== ''}
              onClick={() =>
                void handle(async () => {
                  const reaktion = alsStunden(ziele.reaktion, REAKTION_HOECHSTENS);
                  const loesung = alsStunden(ziele.loesung, LOESUNG_HOECHSTENS);
                  if (reaktion === 'falsch' || loesung === 'falsch') return;
                  await sendeAenderung(entwurf.id, {
                    bezeichnung: entwurf.bezeichnung,
                    absenderAdresse: entwurf.absenderAdresse,
                    absenderName: entwurf.absenderName,
                    antwortAn: entwurf.antwortAn,
                    signatur: entwurf.signatur,
                    zugangsart: entwurf.zugangsart,
                    rueckspiegelung: entwurf.rueckspiegelung,
                    aktiv: entwurf.aktiv,
                    bemerkung: entwurf.bemerkung,
                    // SUP-25
                    reaktionszielStunden: reaktion,
                    loesungszielStunden: loesung,
                    eskalationRolle: entwurf.eskalationRolle,
                    // SUP-27: Leerraum allein ist kein eigener Text.
                    eingangsbestaetigung: entwurf.eingangsbestaetigung,
                    eingangsbestaetigungText:
                      entwurf.eingangsbestaetigungText === null || entwurf.eingangsbestaetigungText.trim() === ''
                        ? null
                        : entwurf.eingangsbestaetigungText,
                  });
                  setzeRueckmeldung(`${entwurf.bezeichnung} wurde gespeichert.`);
                  setzeOffen(undefined);
                  setzeEntwurf(undefined);
                  await laden();
                })
              }
            >
              Speichern
            </button>
          </div>
        </Dialog>
      )}

      {demoFuer !== undefined && (
        <DemoMail
          postfach={demoFuer.kennung}
          bezeichnung={demoFuer.bezeichnung}
          beiSchliessen={() => {
            setzeDemoFuer(undefined);
            eingangLaden();
          }}
        />
      )}
    </>
  );
}

/** SUP-01: ob das Postfach abgerufen wird. Ohne Zugangsdaten ist das kein Fehler, nur „nicht eingerichtet". */
function Eingangszeile({ stand }: { readonly stand: Posteingangsstand | undefined }): JSX.Element | null {
  if (stand === undefined) return null;
  const zeit = (wert: string | null): string =>
    wert === null
      ? 'noch nie'
      : new Date(wert).toLocaleString('de-DE', {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'Europe/Berlin',
      });
  return (
    <Feldzeile
      beschriftung="Abruf"
      wert={null}
      anzeige={
        !stand.eingerichtet ? (
          <>
            <span className={markenklasse('stillgelegt')}>nicht eingerichtet</span>
            <span className="kanalliste__name"> · {stand.variablen.join(', ')}</span>
            {/* CRM-183: etwa Demo-Anbindung außerhalb einer Demo-Instanz. */}
            {stand.hinweis !== null && <span className="feld__fehler"> {stand.hinweis}</span>}
          </>
        ) : stand.adapter === 'demo' && stand.fehler === null ? (
          <>
            <span className={markenklasse('aufmerksamkeit')}>Demo-Anbindung</span> kein Abruf aus dem Netz
            {stand.ungelesen > 0 && ` · ${String(stand.ungelesen)} ungelesen`}
          </>
        ) : stand.fehler !== null ? (
          <>
            <span className={markenklasse('sackgasse')}>gestört</span> {stand.fehler}
          </>
        ) : (
          <>
            <span className={markenklasse('abschluss')}>läuft</span> zuletzt {zeit(stand.erfolgreichAm)}
            {stand.ungelesen > 0 && ` · ${String(stand.ungelesen)} ungelesen`}
          </>
        )
      }
    />
  );
}
